import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { SwPartNumber } from '../../../shared/models/sw-part-number/sw-part-number';
import { map, Observable } from 'rxjs';
import { GroupedSwPartNumber } from '../../../shared/models/sw-part-number/grouped-sw-part-number';
import { Pagination } from '../../../shared/models/internal/pagination';
import { BaseQuery } from '../../../shared/models/base/base-query';
import { BaseApi } from './base/base-api';
import { SwPartNumberUpdateConfirmation } from '../../../shared/models/sw-part-number/sw-part-number-update-confirmation';
import { CommonUtils } from '../../common/common-utils';
import { CreateMajorBranch } from '../../../shared/models/sw-part-number/create-major-branch';
import { CreateSnapshot } from '../../../shared/models/sw-part-number/create-snapshot';
import { SwPartNumberQuery } from '../../../shared/models/sw-part-number/sw-part-number-query';
import { AggreatedLanguageTranslationState } from '../../../shared/models/dtc/aggreated-language-translation-state';
import { HttpContext } from '@angular/common/http';
import { SKIP_SPINNER_CONTEXT_TOKEN } from './skip-spinner-context-token';
import { ImportReport } from '../../../shared/models/report/import-report';
import { DevSwPartNumberWithSameNwNumberCollection } from '../../../shared/models/sw-part-number/dev-sw-part-number-with-same-nw-number-collection';

@Injectable({
  providedIn: 'root',
})
export class SwPartNumberApiService extends BaseApi {
  private commonUtils = CommonUtils;

  constructor() {
    super(environment.api + 'swpartnumber');
  }

  public getSwPartNumbers(): Observable<SwPartNumber[]> {
    return this.http.get<SwPartNumber[]>(this.baseUrl).pipe(
      map((swPartNumbers) => {
        return swPartNumbers.map((swPartNumber) => {
          swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(swPartNumber);
          swPartNumber.extendedDisplayId = `SW Part Number ${swPartNumber.displayId}`;
          return swPartNumber;
        });
      })
    );
  }

  public getGroupedSwPartNumbers(): Observable<GroupedSwPartNumber[]> {
    return this.http.get<GroupedSwPartNumber[]>(this.baseUrl + '/grouped').pipe(
      map((groupedSwPartNumbers) => {
        return groupedSwPartNumbers.map((groupedSwPartNumber) => {
          groupedSwPartNumber.swPartNumbers?.map((swPartNumber) => {
            swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(swPartNumber);
            swPartNumber.extendedDisplayId = `SW Part Number ${swPartNumber.displayId}`;
            return swPartNumber;
          });
          return groupedSwPartNumber;
        });
      })
    );
  }

  public getSwPartNumber(id: string, includeApprovalStats = false): Observable<SwPartNumber> {
    const query = includeApprovalStats ? '?includeApprovalStats=true' : '';
    return this.http.get<SwPartNumber>(this.baseUrl + '/' + id + query).pipe(
      map((swPartNumber) => {
        swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(swPartNumber);
        swPartNumber.extendedDisplayId = `SW Part Number ${swPartNumber.displayId}`;
        return swPartNumber;
      })
    );
  }

  public searchSwPartNumbers(query: SwPartNumberQuery): Observable<Pagination<SwPartNumber>> {
    const queryParams: Record<string, string> = {};

    if (query.page) {
      queryParams['page'] = query.page.toString();
    }

    if (query.pageSize) {
      queryParams['pageSize'] = query.pageSize.toString();
    }

    if (query.orderBy) {
      queryParams['orderBy'] = query.orderBy;
    }

    if (query.orderByDescending !== undefined) {
      queryParams['orderByDescending'] = query.orderByDescending ? 'true' : 'false';
    }

    if (query.number) {
      queryParams['number'] = query.number;
    }

    if (query.searchPattern) {
      queryParams['searchPattern'] = query.searchPattern;
    }

    if (query.ids) {
      queryParams['ids'] = query.ids;
    }

    return this.http
      .get<SwPartNumber[]>(this.baseUrl + '?' + new URLSearchParams(queryParams).toString(), {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const swPartNumbers = response.body ?? [];
          const paginationHeader = response.headers.get(this.xPaginationHeader);

          if (paginationHeader) {
            const pagination = JSON.parse(paginationHeader) as Pagination<SwPartNumber>;
            pagination.items = swPartNumbers.map((swPartNumber) => {
              swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(swPartNumber);
              return swPartNumber;
            });
            return pagination;
          }
          throw new Error('Pagination header not found');
        })
      );
  }

  public getRecycledSwPartNumbers(query: BaseQuery): Observable<Pagination<SwPartNumber>> {
    const queryParams: Record<string, string> = {};

    if (query.page) {
      queryParams['page'] = query.page.toString();
    }

    if (query.pageSize) {
      queryParams['pageSize'] = query.pageSize.toString();
    }

    if (query.orderBy) {
      queryParams['orderBy'] = query.orderBy;
    }

    if (query.orderByDescending !== undefined) {
      queryParams['orderByDescending'] = query.orderByDescending ? 'true' : 'false';
    }

    return this.http
      .get<SwPartNumber[]>(this.baseUrl + '/recycled?' + new URLSearchParams(queryParams).toString(), {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          const swPartNumbers = response.body ?? [];
          const paginationHeader = response.headers.get(this.xPaginationHeader);

          if (paginationHeader) {
            const pagination = JSON.parse(paginationHeader) as Pagination<SwPartNumber>;
            pagination.items = swPartNumbers;
            return pagination;
          }
          throw new Error('Pagination header not found');
        })
      );
  }

  public createNewBranch(createMajorBranch: CreateMajorBranch): Observable<SwPartNumber> {
    return this.http.post<SwPartNumber>(this.baseUrl + '/majorBranch', createMajorBranch).pipe(
      map((swPartNumber) => {
        swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(swPartNumber);
        swPartNumber.extendedDisplayId = `SW Part Number ${swPartNumber.displayId}`;
        return swPartNumber;
      })
    );
  }

  public createSnapshot(createSnapshot: CreateSnapshot): Observable<SwPartNumber> {
    return this.http.post<SwPartNumber>(this.baseUrl + '/snapshot', createSnapshot).pipe(
      map((swPartNumber) => {
        swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(swPartNumber);
        swPartNumber.extendedDisplayId = `SW Part Number ${swPartNumber.displayId}`;
        return swPartNumber;
      })
    );
  }

  public releaseSnapshot(id: string): Observable<SwPartNumber> {
    return this.http.put<SwPartNumber>(this.baseUrl + '/snapshot/' + id, null).pipe(
      map((swPartNumber) => {
        swPartNumber.displayId = this.commonUtils.getFriendlySwPartNumber(swPartNumber);
        swPartNumber.extendedDisplayId = `SW Part Number ${swPartNumber.displayId}`;
        return swPartNumber;
      })
    );
  }

  public deleteSoft(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/softdelete/' + id, null);
  }

  public updateSwPartNumber(swPartNumber: SwPartNumber): Observable<SwPartNumberUpdateConfirmation> {
    return this.http.put<SwPartNumberUpdateConfirmation>(this.baseUrl, swPartNumber);
  }

  public getAggregatedTranslationStates(id: string): Observable<AggreatedLanguageTranslationState> {
    return this.http.get<AggreatedLanguageTranslationState>(this.baseUrl + '/aggregatedtranslationstates/' + id, {
      context: new HttpContext().set(SKIP_SPINNER_CONTEXT_TOKEN, true),
    });
  }

  public exportDefaultLanguageData(id: string): Observable<Blob> {
    return this.http.get(this.baseUrl + '/export-default-language-data/' + id, {
      responseType: 'blob',
    });
  }

  public exportTrDocData(id: string): Observable<Blob> {
    return this.http.get(this.baseUrl + '/export-tr-documentation-fields/' + id, {
      responseType: 'blob',
    });
  }

  public importTranslatedLanguageData(id: string, xmlFiles: File[]): Observable<void> {
    const formData = new FormData();
    xmlFiles.forEach((file) => formData.append('xmlFiles', file));
    return this.http.post<void>(this.baseUrl + '/import-translated-language-data/' + id, formData);
  }

  public importTrDocData(id: string, xmlFiles: File[]): Observable<ImportReport> {
    const formData = new FormData();
    // TODO: BE currently supports only a single file
    const xmlFile = xmlFiles[0];
    formData.append('xmlFile', xmlFile);
    return this.http.post<ImportReport>(this.baseUrl + '/import-tr-documentation-fields/' + id, formData);
  }

  public importMainLanguageData(id: string, xmlFiles: File[]): Observable<void> {
    const formData = new FormData();
    xmlFiles.forEach((file) => formData.append('xmlFiles', file));
    return this.http.post<void>(this.baseUrl + '/import-main-language-data/' + id, formData);
  }

  public resetOnTerminalExportedForTrFlag(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/reset-tr-review-terminal-flags/' + id, null);
  }

  public resetDocumentationExportedForTrFlag(id: string): Observable<void> {
    return this.http.put<void>(this.baseUrl + '/reset-tr-review-documentation-flags/' + id, null);
  }

  public publishSyncEvents(id: string): Observable<void> {
    return this.http.get<void>(this.baseUrl + '/publish-sync-events/' + id);
  }

  public getDevSwPartNumbersWithSameNwNumber(id: string): Observable<DevSwPartNumberWithSameNwNumberCollection> {
    return this.http
      .get<DevSwPartNumberWithSameNwNumberCollection>(this.baseUrl + '/dev-sw-part-numbers-with-same-nw-number/' + id)
      .pipe(
        map((checkGroupCollection) => {
          checkGroupCollection.devSwPartNumbersWithSameNwNumber?.forEach((checkGroup) => {
            checkGroup.number = this.commonUtils.maskSwPartNumberMinorRelease(checkGroup.number) ?? checkGroup.number;
          });
          return checkGroupCollection;
        })
      );
  }
}
